import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import {
  Search, BookOpen, FileText, Calculator,
  ArrowRight, ChevronRight, Sparkles, GraduationCap
} from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const VTU_LOGO = "https://customer-assets.emergentagent.com/job_prism-labs-2/artifacts/s5v94ogg_image.png";
const BMSCE_LOGO = "https://customer-assets.emergentagent.com/job_prism-labs-2/artifacts/czx8x0ru_image.png";

const UNIVERSITIES = [
  { id: "vtu", name: "Visvesvaraya Technological University", short_name: "VTU", location: "Belagavi, Karnataka", image: VTU_LOGO },
  { id: "bmsce", name: "BMS College of Engineering (Autonomous)", short_name: "BMSCE", location: "Bangalore, Karnataka", image: BMSCE_LOGO },
];

export default function HomePage() {
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/stats`).then((res) => {
      setStats(res.data);
    }).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/resources?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/15 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: "1.5s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-500/10 rounded-full blur-[150px]" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeInUp} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-600/10 border border-violet-500/20 text-violet-300 text-xs font-medium tracking-wide">
                <Sparkles className="w-3.5 h-3.5" />
                Platform for Engineering Students
              </span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              data-testid="hero-title"
              className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6"
            >
              <span className="text-white">All Engineering Colleges.</span>
              <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-400 via-cyan-300 to-pink-400">
                One Platform.
              </span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              data-testid="hero-subtitle"
              className="text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed"
            >
              Access notes, question papers, syllabus, placements and tools for engineering students.
            </motion.p>

            <motion.form
              variants={fadeInUp}
              onSubmit={handleSearch}
              className="max-w-xl mx-auto relative"
            >
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                data-testid="hero-search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search colleges, universities or subjects..."
                className="w-full bg-black/50 border border-white/10 focus:border-violet-500/50 rounded-full pl-14 pr-6 py-4 text-white placeholder:text-zinc-600 backdrop-blur-xl outline-none focus:ring-2 focus:ring-violet-500/20 transition-shadow"
              />
              <button
                data-testid="hero-search-btn"
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2 rounded-full bg-violet-600 text-white text-sm font-semibold hover:bg-violet-500 active:scale-95 transition-transform"
              >
                Search
              </button>
            </motion.form>

            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-8 mt-12">
              {[
                { label: "Universities", value: UNIVERSITIES.length },
                { label: "Branches", value: stats.branches || 12 },
                { label: "Resources", value: stats.resources || 0 },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="font-heading text-2xl font-bold text-white">{stat.value}+</div>
                  <div className="text-xs text-zinc-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Universities Section */}
      <section className="py-20" data-testid="universities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeInUp} className="flex items-end justify-between mb-12">
              <div>
                <span className="text-xs font-medium tracking-widest text-violet-400 uppercase mb-2 block">Explore</span>
                <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white">Our Universities</h2>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {UNIVERSITIES.map((uni) => (
                <motion.div key={uni.id} variants={fadeInUp}>
                  <Link
                    to={`/university/${uni.id}`}
                    data-testid={`university-card-${uni.id}`}
                    className="group block rounded-2xl bg-white/[0.03] border border-white/10 hover:border-violet-500/30 hover:-translate-y-1 hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.15)] overflow-hidden"
                    style={{ transitionProperty: "border-color, transform, box-shadow", transitionDuration: "300ms" }}
                  >
                    <div className="flex items-center gap-4 p-6">
                      {uni.image ? (
                        <img src={uni.image} alt={uni.short_name}
                          className="w-16 h-16 rounded-xl object-contain bg-white/5 border border-white/10 p-1.5 shrink-0 group-hover:scale-105"
                          style={{ transitionProperty: "transform", transitionDuration: "300ms" }} />
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-violet-600/10 border border-white/10 flex items-center justify-center shrink-0 group-hover:scale-105"
                          style={{ transitionProperty: "transform", transitionDuration: "300ms" }}>
                          <GraduationCap className="w-7 h-7 text-violet-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-lg font-bold text-white mb-0.5 group-hover:text-violet-200 transition-colors truncate">{uni.short_name}</h3>
                        <p className="text-xs text-zinc-500 truncate">{uni.location}</p>
                        <div className="flex items-center gap-1 text-xs text-violet-400 group-hover:text-violet-300 transition-colors mt-2">
                          Browse Resources <ChevronRight className="w-3 h-3" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Access Section */}
      <section className="py-20" data-testid="quick-access-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <span className="text-xs font-medium tracking-widest text-pink-400 uppercase mb-2 block">Quick Access</span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mb-4">Study Tools</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <motion.div variants={fadeInUp}>
                <Link
                  to="/resources"
                  data-testid="quick-resources"
                  className="group block h-full rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md hover:border-white/20 transition-colors overflow-hidden p-8"
                >
                  <div className="inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 p-3 mb-4 group-hover:scale-110"
                    style={{ transitionProperty: "transform", transitionDuration: "300ms" }}>
                    <BookOpen className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white mb-2">All Resources</h3>
                  <p className="text-sm text-zinc-500 mb-4">Browse notes, PYQs, syllabus and lab manuals</p>
                  <div className="flex items-center gap-1 text-xs text-zinc-500 group-hover:text-violet-400 transition-colors">
                    Explore <ChevronRight className="w-3 h-3" />
                  </div>
                </Link>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <Link
                  to="/resources?resource_type=pyq"
                  data-testid="quick-pyq"
                  className="group block h-full rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md hover:border-white/20 transition-colors overflow-hidden p-8"
                >
                  <div className="inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 p-3 mb-4 group-hover:scale-110"
                    style={{ transitionProperty: "transform", transitionDuration: "300ms" }}>
                    <FileText className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white mb-2">Question Papers</h3>
                  <p className="text-sm text-zinc-500 mb-4">Previous year question papers for exam prep</p>
                  <div className="flex items-center gap-1 text-xs text-zinc-500 group-hover:text-violet-400 transition-colors">
                    Explore <ChevronRight className="w-3 h-3" />
                  </div>
                </Link>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <Link
                  to="/cgpa-calculator"
                  data-testid="quick-cgpa"
                  className="group block h-full rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md hover:border-white/20 transition-colors overflow-hidden p-8"
                >
                  <div className="inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 p-3 mb-4 group-hover:scale-110"
                    style={{ transitionProperty: "transform", transitionDuration: "300ms" }}>
                    <Calculator className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white mb-2">CGPA Calculator</h3>
                  <p className="text-sm text-zinc-500 mb-4">Calculate your CGPA easily</p>
                  <div className="flex items-center gap-1 text-xs text-zinc-500 group-hover:text-violet-400 transition-colors">
                    Calculate <ChevronRight className="w-3 h-3" />
                  </div>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mb-6">
              Ready to ace your exams?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-zinc-400 mb-8">
              Start exploring resources for your university.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-4">
              <Link
                to="/resources"
                data-testid="cta-explore-btn"
                className="px-8 py-3 rounded-full bg-white text-black font-semibold hover:bg-white/90 active:scale-95 transition-transform"
              >
                Explore Resources
              </Link>
              <Link
                to="/cgpa-calculator"
                data-testid="cta-cgpa-btn"
                className="px-8 py-3 rounded-full bg-transparent text-white border border-white/10 hover:bg-white/5 transition-colors"
              >
                CGPA Calculator
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
